/**
 * Unified Tracking API
 *
 * Single entry point for all analytics tracking.
 * Usage:
 *
 *   import { track, trackPageView, trackLead } from '@/lib/tracking';
 *
 *   // Generic tracking
 *   await track('Lead', { job_title: 'Engineer' }, { userEmail: 'a@b.com' });
 *
 *   // Convenience methods
 *   await trackPageView();
 *   await trackLead(params, context);
 */

import { trackingManager } from "./manager";
import { GA4ClientPlugin } from "./plugins/ga4-client";
import { MetaClientPlugin } from "./plugins/meta-client";
import { ServerSidePlugin } from "./plugins/server-side";
import type { EventName, TrackingContext, TrackingResult } from "./types";

// ============================================
// INITIALIZATION
// ============================================

let initPromise: Promise<void> | null = null;

/**
 * Initialize tracking with all plugins.
 * Call this once in your app's root component.
 */
export async function initializeTracking(): Promise<void> {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    // Register plugins
    trackingManager.registerPlugin("ga4-client", new GA4ClientPlugin());
    trackingManager.registerPlugin("meta-client", new MetaClientPlugin());
    trackingManager.registerPlugin("server-side", new ServerSidePlugin());

    // Future platforms - just add plugins:
    // trackingManager.registerPlugin('tiktok', new TikTokPlugin());

    await trackingManager.initialize();
  })();

  return initPromise;
}

// ============================================
// MAIN TRACKING API
// ============================================

/**
 * Track an analytics event.
 *
 * @param eventName - One of the 18 standard event names
 * @param params - Event parameters (value, currency, job_title, etc.)
 * @param context - User/business context for linking
 */
export async function track(
  eventName: EventName,
  params?: Record<string, unknown>,
  context?: TrackingContext
): Promise<TrackingResult> {
  return trackingManager.track(eventName, params, context);
}

// ============================================
// CONVENIENCE METHODS
// ============================================

/**
 * Track page view
 */
export async function trackPageView(
  params?: Record<string, unknown>
): Promise<TrackingResult> {
  return track("PageView", params);
}

/**
 * Track lead submission (application form)
 */
export async function trackLead(
  params: {
    job_title?: string;
    job_landing_page_id?: string;
    funnel_answers?: Record<string, string>;
    availability?: string;
    last_job_experience?: string;
    value?: number;
    currency?: string;
  },
  context: {
    userEmail: string;
    userPhone?: string;
    userName?: string;
    userCity?: string;
    userZip?: string;
    userYear?: string;
    jobLandingPageId: string;
    applicantId?: string;
  }
): Promise<TrackingResult> {
  return track("Lead", params, context);
}

/**
 * Track purchase (subscription)
 */
export async function trackPurchase(
  params: {
    value: number;
    currency?: string;
    plan?: string;
    transaction_id?: string;
    page_id?: string;
  },
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("Purchase", { currency: "CHF", ...params }, context);
}

/**
 * Track user registration completion
 */
export async function trackCompleteRegistration(
  params?: {
    value?: number;
    currency?: string;
    trigger_source?: string;
  },
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("CompleteRegistration", params, context);
}

/**
 * Track email verification
 */
export async function trackVerifyEmail(
  params?: Record<string, unknown>,
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("VerifyEmail", params, context);
}

/**
 * Track begin checkout (payment flow started)
 */
export async function trackBeginCheckout(
  params?: {
    value?: number;
    currency?: string;
    plan?: string;
  },
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("BeginCheckout", params, context);
}

/**
 * Track job page view (public landing page)
 */
export async function trackViewJobListing(params: {
  job_landing_page_id?: string | null;
  job_title?: string | null;
}): Promise<TrackingResult> {
  return track("ViewJobListing", {
    job_landing_page_id: params.job_landing_page_id ?? undefined,
    job_title: params.job_title ?? undefined,
  });
}

/**
 * Track job preview view (before publish)
 */
export async function trackViewJobPreview(
  params: {
    job_landing_page_id?: string | null;
    job_title?: string | null;
  },
  context?: TrackingContext
): Promise<TrackingResult> {
  return track(
    "ViewJobPreview",
    {
      job_landing_page_id: params.job_landing_page_id ?? undefined,
      job_title: params.job_title ?? undefined,
    },
    context
  );
}

/**
 * Track create page button click
 */
export async function trackCreatePageButton(
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("CreatePageButton", undefined, context);
}

/**
 * Track job creation submit
 */
export async function trackCreatePageSubmit(
  params?: {
    job_title?: string;
    value?: number;
    currency?: string;
    companyUrl?: string;
    jobAdUrl?: string;
  },
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("CreatePageSubmit", params, context);
}

/**
 * Track publish button click
 */
export async function trackPublishButton(
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("PublishButton", undefined, context);
}

/**
 * Track job publish submit
 */
export async function trackPublishSubmit(
  params?: {
    job_landing_page_id?: string | null;
    job_title?: string | null;
  },
  context?: TrackingContext
): Promise<TrackingResult> {
  return track(
    "PublishSubmit",
    {
      job_landing_page_id: params?.job_landing_page_id ?? undefined,
      job_title: params?.job_title ?? undefined,
    },
    context
  );
}

/**
 * Track pricing modal view
 */
export async function trackPricingModalView(
  params?: {
    plan?: string;
  },
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("PricingModalView", params, context);
}

/**
 * Track registration page view
 */
export async function trackViewRegistrationPage(
  params?: Record<string, unknown>,
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("ViewRegistrationPage", params, context);
}

/**
 * Track email submission (first step of registration)
 */
export async function trackSubmitEmail(
  params?: Record<string, unknown>,
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("SubmitEmail", params, context);
}

/**
 * Track market check tool usage
 */
export async function trackMarketCheck(
  params?: Record<string, unknown>
): Promise<TrackingResult> {
  return track("MarketCheck", params);
}

/**
 * Track auto-builder form submit
 */
export async function trackAutoBuilderFormSubmit(
  params?: Record<string, unknown>
): Promise<TrackingResult> {
  return track("AutoBuilderFormSubmit", params);
}

/**
 * Track applicant search button click
 */
export async function trackApplicantSearchButton(
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("ApplicantSearchButton", undefined, context);
}

// ============================================
// NEW EVENTS (Added 2026-01-28)
// ============================================

/**
 * Track funnel start (applicant begins questionnaire)
 * Jobs Funnel: ViewJobListing -> FunnelStart -> ViewForm -> Lead -> ViewCVUploadForm -> SubmitApplication
 */
export async function trackFunnelStart(
  params?: Record<string, unknown>
): Promise<TrackingResult> {
  return track("FunnelStart", params);
}

/**
 * Track contact form view (after funnel, before CV upload)
 */
export async function trackViewForm(
  params?: Record<string, unknown>
): Promise<TrackingResult> {
  return track("ViewForm", params);
}

/**
 * Track CV upload form view (after Lead, before SubmitApplication)
 */
export async function trackViewCVUploadForm(
  params?: Record<string, unknown>
): Promise<TrackingResult> {
  return track("ViewCVUploadForm", params);
}

/**
 * Track homepage view
 */
export async function trackViewHomepage(
  params?: Record<string, unknown>,
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("ViewHomepage", params, context);
}

/**
 * Track needs verification modal view (when is_active=False on login)
 */
export async function trackViewNeedsVerification(
  params?: Record<string, unknown>,
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("ViewNeedsVerification", params, context);
}

/**
 * Track job creation form view (tutorial step-2)
 */
export async function trackViewJobCreationForm(
  params?: Record<string, unknown>,
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("ViewJobCreationForm", params, context);
}

/**
 * Track exit modal view (draft users - "publishing is free")
 */
export async function trackExitModalView(
  params?: Record<string, unknown>,
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("ExitModalView", params, context);
}

/**
 * Track market check modal view (published unpaid - "applicants you're losing")
 */
export async function trackMarketCheckModalView(
  params?: Record<string, unknown>,
  context?: TrackingContext
): Promise<TrackingResult> {
  return track("MarketCheckModalView", params, context);
}

// ============================================
// DEMO VIDEO EVENTS
// ============================================

/**
 * Track demo video play (modal open or inline play)
 */
export async function trackDemoVideoPlay(params?: {
  trigger_source?: string;
  video?: string;
}): Promise<TrackingResult> {
  return track("DemoVideoPlay", params);
}

/**
 * Track demo video progress milestone (25%, 50%, 75%)
 */
export async function trackDemoVideoProgress(params: {
  percent: number;
  video?: string;
}): Promise<TrackingResult> {
  return track("DemoVideoProgress", params);
}

/**
 * Track demo video completion
 */
export async function trackDemoVideoComplete(params?: {
  video?: string;
}): Promise<TrackingResult> {
  return track("DemoVideoComplete", params);
}

// ============================================
// CONSTANTS (for script loading)
// ============================================

export const GA_TRACKING_ID = "G-C6FWHK8HR3";
export const FB_PIXEL_ID = "757665840533275";
export const GTM_ID = "GTM-ND5CJWF2";

/**
 * Load Google Tag Manager (Google's official loader). Uses the default
 * dataLayer shared with gtag, so GTM sees the existing events. Idempotent.
 * Only call after the visitor accepted cookies (see CookieConsentBanner).
 */
export function loadGtm(): void {
  if (document.getElementById("gtm-loader")) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.id = "gtm-loader";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}

/**
 * Load the Meta Pixel library. The fbq stub in ClientLayout queues calls
 * until then, so earlier events are sent once it loads. Idempotent.
 * Only call after the visitor accepted cookies (see CookieConsentBanner).
 */
export function loadMetaPixel(): void {
  if (document.getElementById("meta-pixel-loader")) return;
  const script = document.createElement("script");
  script.id = "meta-pixel-loader";
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
}

// ============================================
// RE-EXPORTS
// ============================================

export type { EventName, TrackingContext, TrackingResult } from "./types";
export { AttributionCapture } from "./attribution";
export { EventRegistry, getEventConfig } from "./registry";
export { buildEmployerContext } from "./employer-context";
